import { useContext, type Component, type JSX } from 'solid-js'
import { PerspectiveCamera, Vector2 } from 'three'
import type { Engine } from '../../lib/core/engine/Engine.type'
import { getEngineThreeMainCamera } from '../../lib/three/fn/getSetEngineThreeMainCamera'
import { getEngineThreeMainRenderer } from '../../lib/three/fn/getSetEngineThreeMainRenderer'
import useMainScene from '../../lib/three/fn/useMainScene'
import { getDeviceUserPreference } from '../../rx/shared/device/userPreference/deviceUserPreferencesSignal'
import EngineContext from '../EngineContext'
import { RESOLUTION_480p } from './resolutions'

interface DownloadCanvasButtonProps {
  filename?: string
  children?: JSX.Element
  class?: string
}

const RecordScreenshotButton: Component<DownloadCanvasButtonProps> = (props) => {
  const engine: Engine = useContext(EngineContext)

  const scene = useMainScene(engine)
  const renderer = getEngineThreeMainRenderer(engine)
  const originalCamera = getEngineThreeMainCamera(engine)

  if (!renderer || !(originalCamera instanceof PerspectiveCamera)) {
    // eslint-disable-next-line solid/components-return-once
    return <div>Renderer or compatible camera not found</div>
  }
  
  // We know this is a PerspectiveCamera now
  const camera = originalCamera as PerspectiveCamera

  const handleDownload = async () => {
    if (!renderer || !scene || !camera) {
      throw new Error('Renderer, scene, or camera not found')
    }
    
    // Store original renderer settings
    const originalPixelRatio = renderer.getPixelRatio()
    const originalSize = new Vector2()
    renderer.getSize(originalSize)
    
    try {
      const { width, height } = RESOLUTION_480p
      
      // Create a temporary camera with the same properties as the current camera
      const tempCamera = camera.clone()
      
      // Adjust camera aspect ratio for 4K and ensure correct FOV
      tempCamera.aspect = width / height
      tempCamera.fov = getDeviceUserPreference().camera_fov
      tempCamera.updateProjectionMatrix()
      tempCamera.layers.disable(1)

      // Set renderer to 4K resolution
      renderer.setPixelRatio(1) // Use 1 to avoid DPI scaling
      renderer.setSize(width, height, false)
      
      // Render the scene with the temporary camera
      renderer.render(scene, tempCamera)
      
      // Get the canvas and download
      const canvas = renderer.domElement
      downloadCanvasAsPNG(canvas, props.filename || 'screenshot-4k.png')
      
    } catch (error) {
      console.error('Error capturing 4K screenshot:', error)
    } finally {
      // Restore original renderer settings
      renderer.setPixelRatio(originalPixelRatio)
      renderer.setSize(originalSize.width, originalSize.height, false)
      camera.aspect = originalSize.width / originalSize.height
      camera.updateProjectionMatrix()
      
      // Re-render with original settings
      renderer.render(scene, camera)
    }
  }

  return (
    <button 
      onClick={handleDownload}
      class={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 ${props.class || ''}`}
      aria-label="Download canvas as PNG"
    >
      {props.children || 'Download PNG'}
    </button>
  )
}

export default RecordScreenshotButton

function downloadCanvasAsPNG(canvas: HTMLCanvasElement, filename: string = 'canvas-image.png'): void {
  canvas.toBlob((blob) => {
    if (!blob) {
      console.error('Failed to create blob from canvas');
      return;
    }
    
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 'image/png');
}